/**
 * Unit of Measure definitions for the platform
 * Organized by type → subtypes with symbols
 */

export interface UnitSubtype {
  key: string;
  symbol: string;
}

export interface UnitType {
  key: string;
  subtypes: UnitSubtype[];
}

export const UNIT_TYPES: UnitType[] = [
  {
    key: 'weight',
    subtypes: [
      { key: 'mg', symbol: 'mg' },
      { key: 'g', symbol: 'g' },
      { key: 'kg', symbol: 'kg' },
      { key: 't', symbol: 't' },
      { key: 'oz', symbol: 'oz' },
      { key: 'lb', symbol: 'lb' },
    ],
  },
  {
    key: 'length',
    subtypes: [
      { key: 'mm', symbol: 'mm' },
      { key: 'cm', symbol: 'cm' },
      { key: 'm', symbol: 'm' },
      { key: 'km', symbol: 'km' },
      { key: 'in', symbol: 'in' },
      { key: 'ft', symbol: 'ft' },
      { key: 'yd', symbol: 'yd' },
      { key: 'mi', symbol: 'mi' },
    ],
  },
  {
    key: 'volume',
    subtypes: [
      { key: 'ml', symbol: 'ml' },
      { key: 'cl', symbol: 'cl' },
      { key: 'dl', symbol: 'dl' },
      { key: 'l', symbol: 'L' },
      { key: 'hl', symbol: 'hL' },
      { key: 'floz', symbol: 'fl oz' },
      { key: 'cup', symbol: 'cup' },
      { key: 'pt', symbol: 'pt' },
      { key: 'qt', symbol: 'qt' },
      { key: 'gal', symbol: 'gal' },
    ],
  },
  {
    key: 'area',
    subtypes: [
      { key: 'sqcm', symbol: 'cm²' },
      { key: 'sqm', symbol: 'm²' },
      { key: 'ha', symbol: 'ha' },
      { key: 'sqkm', symbol: 'km²' },
      { key: 'sqft', symbol: 'ft²' },
      { key: 'sqyd', symbol: 'yd²' },
      { key: 'ac', symbol: 'ac' },
      { key: 'sqmi', symbol: 'mi²' },
    ],
  },
  {
    key: 'quantity',
    subtypes: [
      { key: 'pcs', symbol: 'pcs' },
      { key: 'pairs', symbol: 'pairs' },
      { key: 'sets', symbol: 'sets' },
      { key: 'packs', symbol: 'packs' },
      { key: 'boxes', symbol: 'boxes' },
      { key: 'rolls', symbol: 'rolls' },
      { key: 'sheets', symbol: 'sheets' },
      { key: 'bottles', symbol: 'bottles' },
      { key: 'cans', symbol: 'cans' },
      { key: 'bags', symbol: 'bags' },
      { key: 'dozens', symbol: 'dz' },
    ],
  },
  {
    key: 'digital',
    subtypes: [
      { key: 'b', symbol: 'B' },
      { key: 'kb', symbol: 'KB' },
      { key: 'mb', symbol: 'MB' },
      { key: 'gb', symbol: 'GB' },
      { key: 'tb', symbol: 'TB' },
    ],
  },
  {
    key: 'time',
    subtypes: [
      { key: 'sec', symbol: 's' },
      { key: 'min', symbol: 'min' },
      { key: 'hr', symbol: 'h' },
      { key: 'day', symbol: 'd' },
      { key: 'wk', symbol: 'wk' },
      { key: 'mo', symbol: 'mo' },
      { key: 'yr', symbol: 'yr' },
    ],
  },
  {
    key: 'energy',
    subtypes: [
      { key: 'wh', symbol: 'Wh' },
      { key: 'kwh', symbol: 'kWh' },
      { key: 'cal', symbol: 'cal' },
      { key: 'kcal', symbol: 'kcal' },
      { key: 'j', symbol: 'J' },
      { key: 'kj', symbol: 'kJ' },
    ],
  },
  {
    key: 'temperature',
    subtypes: [
      { key: 'c', symbol: '°C' },
      { key: 'f', symbol: '°F' },
      { key: 'k', symbol: 'K' },
    ],
  },
  {
    key: 'speed',
    subtypes: [
      { key: 'kmh', symbol: 'km/h' },
      { key: 'mph', symbol: 'mph' },
      { key: 'ms', symbol: 'm/s' },
      { key: 'kn', symbol: 'kn' },
    ],
  },
  {
    key: 'custom',
    subtypes: [],
  },
];

/**
 * Get a unit type by key
 */
export const getUnitType = (key: string): UnitType | undefined =>
  UNIT_TYPES.find(u => u.key === key);

/**
 * Get subtypes for a given unit type key
 */
export const getSubtypes = (typeKey: string): UnitSubtype[] =>
  UNIT_TYPES.find(u => u.key === typeKey)?.subtypes || [];

/**
 * Get the symbol for a given subtype key
 */
export const getUnitSymbol = (subtypeKey: string): string => {
  for (const type of UNIT_TYPES) {
    const sub = type.subtypes.find(s => s.key === subtypeKey);
    if (sub) return sub.symbol;
  }
  return subtypeKey;
};

/**
 * Format a quantity with its unit symbol
 */
export const formatQuantity = (quantity: number | null | undefined, subtypeKey: string | null | undefined): string => {
  if (!quantity && quantity !== 0) return '';
  const symbol = subtypeKey ? getUnitSymbol(subtypeKey) : '';
  return symbol ? `${quantity} ${symbol}` : `${quantity}`;
};
